import React, { useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { interpretTTE, interpretTAE } from '../../utils/interpretTraining';
import { useTranslation } from '../../i18n';

const SwimmingPhysiologicalAnalysis = ({
  completed = {},
  metrics = {},
  laps = [],
  athleteId = null,
  activityId = null,
  date = null,
  banister = null,
}) => {
  const { t, language } = useTranslation();

  const parseDurationToSeconds = (dur) => {
    if (!dur) return 0;
    if (typeof dur === 'string' && dur.includes(':')) {
      const parts = dur.split(':').map(Number);
      return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
    }
    return parseFloat(dur) || 0;
  };

  const distance    = parseFloat(completed.distancia) || 0;
  const durationSec = parseDurationToSeconds(completed.duracion);


  const vo2      = parseFloat(completed.vo2max) || 0;
  const tss      = parseFloat(completed.tss);
  const tte      = parseFloat(completed.tte);
  const tea      = parseFloat(completed.tea);
  const swolfVal = Number.isFinite(parseFloat(completed.swolf)) ? parseFloat(parseFloat(completed.swolf).toFixed(1)) : 0;
  const durationMin = durationSec ? (durationSec / 60).toFixed(2) : 'N/A';

  useEffect(() => {
    if (!completed || Object.keys(completed).length === 0) return;

    const existingChart = am5.registry.rootElements.find((r) => r.dom?.id === 'chartdiv-swimming');
    if (existingChart) existingChart.dispose();

    const root  = am5.Root.new('chartdiv-swimming');
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, { layout: root.verticalLayout })
    );

    chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', xAxis: null, yAxis: null }));

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'category',
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 30 }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}) })
    );

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'valor', xAxis, yAxis,
        valueYField: 'value', categoryXField: 'category',
        tooltip: am5.Tooltip.new(root, { labelText: '{categoryX}: {valueY.formatNumber("#.00")}' }),
      })
    );

    series.columns.template.setAll({
      tooltipY: 0, interactive: true,
      cornerRadiusTL: 6, cornerRadiusTR: 6,
      fill: am5.color(0x27aae1), stroke: am5.color(0x193255),
    });

    const data = [
      { category: 'TSS',    value: Number.isFinite(tss)      ? tss      : 0 },
      { category: 'TTE',    value: Number.isFinite(tte)      ? tte      : 0 },
      { category: 'TEA',    value: Number.isFinite(tea)      ? tea      : 0 },
      { category: 'VO₂ max',value: Number.isFinite(vo2)      ? vo2      : 0 },
      { category: 'Swolf',  value: Number.isFinite(swolfVal) ? swolfVal : 0 },
    ];

    xAxis.data.setAll(data);
    series.data.setAll(data);

    return () => { root.dispose(); };
  }, [completed, laps, tss, tte, tea, vo2, swolfVal]);

  const format = (val, fixed = 2) => (!Number.isNaN(val) ? Number(val).toFixed(fixed) : 'N/A');

  const loadLabel = tss >= 100
    ? t('swimming.physioLoadHigh')
    : tss >= 75
    ? t('swimming.physioLoadModHigh')
    : t('swimming.physioLoadMod');

  return (
    <div className="mt-4">
      <h5 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>📊 {t('athleteDashboard.runningPhysio.title')}</h5>
      {vo2 > 0 && <p>{t('athleteDashboard.runningPhysio.vo2Sentence', { value: format(vo2) })}</p>}
      <p>{t('athleteDashboard.runningPhysio.tssSentence', { tss: format(tss), duration: durationMin, load: tss >= 100 ? t('athleteDashboard.runningPhysio.loadHigh') : t('athleteDashboard.runningPhysio.loadModerate') })}</p>
      {metrics?.ritmo_cardiaco?.avg && <p>{t('athleteDashboard.runningPhysio.hrSentence', { avg: metrics.ritmo_cardiaco.avg, max: metrics.ritmo_cardiaco.max })}</p>}
      {!isNaN(tte) && tte > 0 && <p>{t('athleteDashboard.runningPhysio.tteSentence', { value: format(tte) })} <em>{interpretTTE(tte, language)}</em></p>}
      {!isNaN(tea) && tea > 0 && <p>{t('athleteDashboard.runningPhysio.teaSentence', { value: format(tea) })} <em>{interpretTAE(tea, language)}</em></p>}
      <div className="mt-4">
        <h6 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>📈 {t('athleteDashboard.runningPhysio.chartTitle')}</h6>
        <div id="chartdiv-swimming" style={{ width: '100%', height: '400px' }} />
      </div>
    </div>
  );
};
export default SwimmingPhysiologicalAnalysis;
